/* Chavu — chavu-animations.js
   Luxury GSAP + ScrollTrigger motion. Soft, slow, eye-soothing.
   Falls back to IntersectionObserver reveals in global.js when GSAP fails. */
(() => {
  'use strict';

  const EASE_OUT = 'power3.out';
  const EASE_SOFT = 'power2.out';

  function revealEverything() {
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('revealed'));
    document.querySelectorAll('[data-about-story]').forEach((root) => {
      root.classList.add('is-ready');
      root.querySelectorAll('.about-story-el, .about-story-line, .about-story-media').forEach((el) => {
        el.style.opacity = '1';
        el.style.filter = 'none';
        el.style.transform = 'none';
      });
    });
  }

  function fallback() {
    document.documentElement.removeAttribute('data-gsap');
    if (window.chavuInitReveal) {
      window.chavuInitReveal();
    } else {
      revealEverything();
    }
  }

  function softReveal(els, opts = {}) {
    const items = gsap.utils.toArray(els).filter(Boolean);
    if (!items.length) return;

    items.forEach((el) => el.classList.add('revealed'));
    gsap.set(items, {
      autoAlpha: 0,
      y: opts.y ?? 36,
      filter: opts.blur ? `blur(${opts.blur}px)` : 'none',
    });

    ScrollTrigger.batch(items, {
      start: opts.start || 'top 88%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: opts.duration ?? 1.45,
          ease: opts.ease || EASE_OUT,
          stagger: opts.stagger ?? 0.12,
          overwrite: true,
        }),
    });
  }

  function init() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      fallback();
      return;
    }

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.body.classList.contains('no-reveal')
    ) {
      revealEverything();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    document.documentElement.setAttribute('data-gsap', '');

    initHero();
    initReveals();
    initSectionHeaders();
    initCollage();
    initImageWithText();
    initCards();
    initMarquee();
    initParallax();
    initAboutStorytelling();
    initProduct();
    initMenuDrawer();

    requestAnimationFrame(() => ScrollTrigger.refresh());
  }

  function animateScope(scope) {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      if (window.chavuInitReveal) window.chavuInitReveal(scope);
      return;
    }

    const root = scope || document;
    const reveals = root.querySelectorAll('.reveal');
    reveals.forEach((el) => {
      el.classList.add('revealed');
      gsap.fromTo(
        el,
        { autoAlpha: 0, y: 28, filter: 'blur(4px)' },
        {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.2,
          ease: EASE_OUT,
          scrollTrigger: {
            trigger: el,
            start: 'top 92%',
            once: true,
          },
        }
      );
    });

    const cards = root.querySelectorAll('.grid.reveal > li, .slider.reveal .slider__slide');
    if (cards.length) {
      gsap.fromTo(
        cards,
        { autoAlpha: 0, y: 24 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 1.15,
          ease: EASE_OUT,
          stagger: 0.08,
          scrollTrigger: {
            trigger: cards[0].closest('.grid, .slider') || cards[0],
            start: 'top 90%',
            once: true,
          },
        }
      );
    }

    ScrollTrigger.refresh();
  }

  /* —— Hero: soft settle —— */
  function initHero() {
    const hero = document.querySelector(
      'main .shopify-section:first-child .banner, .shopify-section:first-of-type .banner'
    );
    if (!hero) return;

    const media = hero.querySelector('.banner__media img, .banner__media video');
    const box = hero.querySelector('.banner__box');
    const content = hero.querySelectorAll('.banner__box > *');

    if (box) {
      box.classList.add('revealed');
      gsap.set(box, { autoAlpha: 1, y: 0, clearProps: 'filter' });
    }

    if (media) {
      gsap.fromTo(
        media,
        { scale: 1.14, filter: 'blur(8px)' },
        { scale: 1, filter: 'blur(0px)', duration: 2.8, ease: EASE_SOFT }
      );
    }

    if (content.length) {
      gsap.fromTo(
        content,
        { autoAlpha: 0, y: 28, filter: 'blur(6px)' },
        {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.7,
          ease: EASE_OUT,
          stagger: 0.18,
          delay: 0.35,
        }
      );
    }
  }

  /* —— Generic .reveal —— */
  function initReveals() {
    const reveals = gsap.utils.toArray('.reveal').filter((el) => {
      if (el.classList.contains('collage')) return false;
      if (el.classList.contains('banner__box')) return false;
      if (el.closest('[data-about-story]')) return false;
      return true;
    });
    if (!reveals.length) return;

    reveals.forEach((el) => el.classList.add('revealed'));
    gsap.set(reveals, { autoAlpha: 0, y: 40, filter: 'blur(5px)' });

    ScrollTrigger.batch(reveals, {
      start: 'top 90%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.55,
          ease: EASE_OUT,
          stagger: 0.14,
          overwrite: true,
        }),
    });
  }

  /* —— Section titles: eyebrow → heading cascade —— */
  function initSectionHeaders() {
    gsap.utils.toArray('.title-wrapper:not(.reveal), .collage-header:not(.reveal)').forEach((wrap) => {
      const parts = wrap.querySelectorAll('.eyebrow, h2, .h2, .collage-header__text');
      if (parts.length < 2) return;

      gsap.set(parts, { autoAlpha: 0, y: 22 });
      ScrollTrigger.create({
        trigger: wrap,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(parts, {
            autoAlpha: 1,
            y: 0,
            duration: 1.35,
            ease: EASE_OUT,
            stagger: 0.14,
          }),
      });
    });

    gsap.utils.toArray('.title-wrapper.reveal, .collage-header.reveal').forEach((wrap) => {
      const parts = wrap.querySelectorAll('.eyebrow, h2, .h2, .collage-header__text');
      if (parts.length < 2) return;
      gsap.set(parts, { autoAlpha: 0, y: 18 });
      ScrollTrigger.create({
        trigger: wrap,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(parts, {
            autoAlpha: 1,
            y: 0,
            duration: 1.25,
            ease: EASE_OUT,
            stagger: 0.12,
            delay: 0.08,
          }),
      });
    });
  }

  /* —— Collage tiles —— */
  function initCollage() {
    const collage = document.querySelector('.collage');
    const items = gsap.utils.toArray('.collage__item');
    if (!items.length) return;

    if (collage) {
      collage.classList.add('revealed');
      gsap.set(collage, { autoAlpha: 1, clearProps: 'filter,transform' });
    }

    items.forEach((item, i) => {
      const content = item.querySelectorAll('.collage__eyebrow, .collage__heading, .collage__text, .collage__cta');
      const img = item.querySelector('img');

      gsap.set(item, { autoAlpha: 0, y: 36 });
      gsap.set(content, { autoAlpha: 0, y: 14 });

      ScrollTrigger.create({
        trigger: item,
        start: 'top 92%',
        once: true,
        onEnter: () => {
          gsap.to(item, {
            autoAlpha: 1,
            y: 0,
            duration: 1.45,
            ease: EASE_OUT,
            delay: i * 0.07,
          });
          if (img) {
            gsap.fromTo(
              img,
              { scale: 1.08 },
              { scale: 1, duration: 1.9, ease: EASE_SOFT, delay: i * 0.07 }
            );
          }
          gsap.to(content, {
            autoAlpha: 1,
            y: 0,
            duration: 1.1,
            ease: EASE_SOFT,
            stagger: 0.08,
            delay: 0.28 + i * 0.07,
          });
        },
      });
    });
  }

  /* —— Product details —— */
  function initProduct() {
    const root = document.querySelector('[data-product-animate]');
    if (!root) return;

    const mediaItems = root.querySelectorAll('.product__media-item');
    const infoBlocks = root.querySelectorAll('.product-block, .product__info > *:not(script)');

    if (mediaItems.length) {
      gsap.set(mediaItems, { autoAlpha: 0, y: 28, filter: 'blur(5px)' });
      gsap.to(mediaItems, {
        autoAlpha: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 1.5,
        ease: EASE_OUT,
        stagger: 0.12,
        delay: 0.08,
      });

      mediaItems.forEach((item) => {
        const img = item.querySelector('img');
        if (!img) return;
        gsap.fromTo(
          img,
          { scale: 1.06 },
          { scale: 1, duration: 2.1, ease: EASE_SOFT, delay: 0.1 }
        );
      });
    }

    if (infoBlocks.length) {
      gsap.set(infoBlocks, { autoAlpha: 0, y: 22 });
      gsap.to(infoBlocks, {
        autoAlpha: 1,
        y: 0,
        duration: 1.25,
        ease: EASE_OUT,
        stagger: 0.09,
        delay: 0.22,
      });
    }
  }

  /* —— Image with text media soft-scale (opacity via .reveal) —— */
  function initImageWithText() {
    gsap.utils.toArray('.image-with-text__media img').forEach((img) => {
      gsap.fromTo(
        img,
        { scale: 1.08 },
        {
          scale: 1,
          duration: 2.1,
          ease: EASE_SOFT,
          scrollTrigger: {
            trigger: img,
            start: 'top 85%',
            once: true,
          },
        }
      );
    });
  }

  /* —— Product / article cards —— */
  function initCards() {
    gsap.utils.toArray('.grid.reveal, .slider.reveal').forEach((list) => {
      const cards = list.querySelectorAll(':scope > li, :scope > .card, .slider__slide, .multicolumn__item');
      if (cards.length < 2) return;

      gsap.set(cards, { autoAlpha: 0, y: 28 });
      ScrollTrigger.create({
        trigger: list,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(cards, {
            autoAlpha: 1,
            y: 0,
            duration: 1.35,
            ease: EASE_OUT,
            stagger: 0.1,
            delay: 0.12,
          }),
      });
    });

    gsap.utils.toArray('.testimonials__list, .multicolumn__grid').forEach((list) => {
      const cards = list.children;
      if (!cards || cards.length < 2) return;
      gsap.set(cards, { autoAlpha: 0, y: 28 });
      ScrollTrigger.create({
        trigger: list,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(cards, {
            autoAlpha: 1,
            y: 0,
            duration: 1.35,
            ease: EASE_OUT,
            stagger: 0.12,
          }),
      });
    });
  }

  /* —— Marquee fade-in —— */
  function initMarquee() {
    gsap.utils.toArray('.marquee').forEach((el) => {
      gsap.set(el, { autoAlpha: 0, y: 18 });
      ScrollTrigger.create({
        trigger: el,
        start: 'top 92%',
        once: true,
        onEnter: () =>
          gsap.to(el, {
            autoAlpha: 1,
            y: 0,
            duration: 1.5,
            ease: EASE_SOFT,
          }),
      });
    });
  }

  /* —— Gentle parallax —— */
  function initParallax() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const saveData = connection && (connection.saveData || /2g/.test(connection.effectiveType || ''));
    if (saveData) return;

    gsap.utils.toArray('.banner__media img').forEach((img) => {
      const banner = img.closest('.banner');
      if (!banner) return;
      gsap.fromTo(
        img,
        { yPercent: -5 },
        {
          yPercent: 5,
          ease: 'none',
          scrollTrigger: {
            trigger: banner,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.4,
          },
        }
      );
    });

    gsap.utils.toArray('.about-page__portrait-frame img, .about-page__diva-media img').forEach((img) => {
      gsap.fromTo(
        img,
        { yPercent: -3, scale: 1.04 },
        {
          yPercent: 3,
          scale: 1.04,
          ease: 'none',
          scrollTrigger: {
            trigger: img.closest('.about-page__portrait, .about-page__diva-media') || img,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1.6,
          },
        }
      );
    });
  }

  /* —— About storytelling —— */
  function initAboutStorytelling() {
    const root = document.querySelector('[data-about-story]');
    if (!root) return;

    const lines = [];
    root.querySelectorAll('.about-story-body').forEach((body) => {
      Array.from(body.children).forEach((node) => {
        if (node.nodeType !== 1) return;
        node.classList.add('about-story-line');
        lines.push(node);
      });
    });

    const els = gsap.utils.toArray(root.querySelectorAll('.about-story-el'));
    const media = gsap.utils.toArray(root.querySelectorAll('.about-story-media'));
    const brand = root.querySelector('.about-page__brand');

    gsap.set(els, { autoAlpha: 0, y: 32, filter: 'blur(6px)' });
    gsap.set(lines, { autoAlpha: 0, y: 26, filter: 'blur(5px)' });
    gsap.set(media, { autoAlpha: 0, scale: 1.05, filter: 'blur(8px)' });
    root.classList.add('is-ready');

    if (brand) {
      gsap.fromTo(
        brand,
        { autoAlpha: 0, y: 36, filter: 'blur(8px)' },
        {
          autoAlpha: 1,
          y: 0,
          filter: 'blur(0px)',
          duration: 1.6,
          ease: EASE_OUT,
          delay: 0.12,
        }
      );
    }

    els.forEach((el) => {
      if (el === brand) return;
      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: () =>
          gsap.to(el, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 1.55,
            ease: EASE_OUT,
            overwrite: 'auto',
          }),
      });
    });

    lines.forEach((line, i) => {
      ScrollTrigger.create({
        trigger: line,
        start: 'top 90%',
        once: true,
        onEnter: () =>
          gsap.to(line, {
            autoAlpha: 1,
            y: 0,
            filter: 'blur(0px)',
            duration: 1.65,
            ease: EASE_SOFT,
            delay: Math.min(i * 0.03, 0.2),
            overwrite: 'auto',
          }),
      });
    });

    media.forEach((frame) => {
      const img = frame.querySelector('img');
      ScrollTrigger.create({
        trigger: frame,
        start: 'top 84%',
        once: true,
        onEnter: () => {
          gsap.to(frame, {
            autoAlpha: 1,
            scale: 1,
            filter: 'blur(0px)',
            duration: 2,
            ease: EASE_OUT,
            overwrite: 'auto',
          });
          if (img) {
            gsap.fromTo(img, { scale: 1.1 }, { scale: 1, duration: 2.4, ease: EASE_SOFT });
          }
        },
      });
    });

    const video = root.querySelector('.about-page__video');
    if (video) {
      const activateVideo = () => {
        video.play().catch(() => {});
      };

      ScrollTrigger.create({
        trigger: video.closest('.about-page__film') || video,
        start: 'top 80%',
        end: 'bottom 20%',
        onEnter: activateVideo,
        onEnterBack: () => video.play().catch(() => {}),
        onLeave: () => video.pause(),
        onLeaveBack: () => video.pause(),
      });
    }
  }

  /* —— Mobile menu drawer —— */
  function initMenuDrawer() {
    const drawer = document.getElementById('MenuDrawer');
    if (!drawer) return;

    const header = drawer.querySelector('.drawer__header');
    const utility = drawer.querySelector('.menu-drawer__utility');
    let tl;

    const targets = () =>
      [header, ...drawer.querySelectorAll('.menu-drawer__item'), utility].filter(Boolean);

    gsap.set(targets(), { autoAlpha: 0, x: -32, filter: 'blur(4px)' });

    drawer.addEventListener('drawer:open', () => {
      const items = drawer.querySelectorAll('.menu-drawer__item');
      if (tl) tl.kill();

      gsap.set(header, { autoAlpha: 0, x: -22, filter: 'blur(4px)' });
      gsap.set(items, { autoAlpha: 0, x: -36, filter: 'blur(5px)' });
      if (utility) gsap.set(utility, { autoAlpha: 0, x: -20, filter: 'blur(4px)' });

      tl = gsap.timeline({ defaults: { ease: EASE_OUT, overwrite: 'auto' } });

      if (header) {
        tl.to(
          header,
          { autoAlpha: 1, x: 0, filter: 'blur(0px)', duration: 0.55 },
          0.08
        );
      }

      tl.to(
        items,
        {
          autoAlpha: 1,
          x: 0,
          filter: 'blur(0px)',
          duration: 0.7,
          stagger: 0.085,
        },
        0.16
      );

      if (utility) {
        tl.to(
          utility,
          { autoAlpha: 1, x: 0, filter: 'blur(0px)', duration: 0.6 },
          '-=0.28'
        );
      }
    });

    drawer.addEventListener('drawer:close', () => {
      if (tl) tl.kill();
      gsap.set(targets(), { autoAlpha: 0, x: -32, filter: 'blur(4px)' });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    animateScope(event.target);
  });

  document.addEventListener('chavu:section:reveal', (event) => {
    animateScope(event.detail && event.detail.target);
  });
})();
